import { useMemo, useState } from 'react';
import { CATALOGUE } from '../data/plates';
import { JURISDICTIONS, JURISDICTION_ORDER, type Jurisdiction } from '../data/jurisdictions';
import { PlateRow } from '../components/PlateRow';
import { ScoreHeader } from '../components/ScoreHeader';
import { useGame, useActiveTrip } from '../state/GameProvider';

type Filter = Jurisdiction | 'ALL';

/**
 * The home screen, because lifetime mode is the default - you open the app and
 * you are already playing, with no session to start.
 *
 * Unclaimed plates sort to the top, cheapest first, so the ones you are most
 * likely to actually see on the next stretch of road are the ones under your
 * thumb.
 */
export function Collection() {
  const { state, claim, undo, canClaim } = useGame();
  const trip = useActiveTrip();
  const [filter, setFilter] = useState<Filter>('ALL');
  const [query, setQuery] = useState('');

  // Which ledger decides "already claimed" depends on the mode you are in.
  const claimedIds = trip ? trip.myClaims : state.lifetime;

  const plates = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return CATALOGUE
      .filter((plate) => filter === 'ALL' || plate.jurisdiction === filter)
      .filter((plate) => {
        if (!needle) return true;
        return (
          plate.name.toLowerCase().includes(needle) ||
          plate.lookFor.toLowerCase().includes(needle)
        );
      })
      .sort((a, b) => {
        const aClaimed = a.id in claimedIds ? 1 : 0;
        const bClaimed = b.id in claimedIds ? 1 : 0;
        if (aClaimed !== bClaimed) return aClaimed - bClaimed;
        return a.points - b.points;
      });
  }, [filter, query, claimedIds]);

  const remaining = plates.filter((p) => !(p.id in claimedIds)).length;

  return (
    <>
      <ScoreHeader />

      <div className="screen">
        <div className="filters" role="group" aria-label="Filter by state">
          <button
            type="button"
            className="chip"
            aria-pressed={filter === 'ALL'}
            onClick={() => setFilter('ALL')}
          >
            All
          </button>
          {JURISDICTION_ORDER.map((code) => (
            <button
              key={code}
              type="button"
              className="chip"
              aria-pressed={filter === code}
              onClick={() => setFilter(code)}
            >
              {JURISDICTIONS[code].short}
            </button>
          ))}
        </div>

        <input
          className="search"
          type="search"
          inputMode="search"
          placeholder="Search plates: taxi, diplomatic, Tasmania…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search plates"
        />

        <p className="screen-sub" style={{ marginTop: 12 }}>
          {remaining === 0
            ? 'Everything here is claimed. Try another state.'
            : `${remaining} still to find${trip ? ' on this trip' : ''}.`}
        </p>

        <div className="plate-list">
          {plates.map((plate) => (
            <PlateRow
              key={plate.id}
              plate={plate}
              claimed={!canClaim(plate.id)}
              onClaim={claim}
              onUndo={undo}
            />
          ))}
        </div>

        {plates.length === 0 && (
          <div className="empty">No plates match that search.</div>
        )}
      </div>
    </>
  );
}
